"use client";

import React from "react";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Layers3, Database, Palette, Rocket, CheckCircle2 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  id: "01",
  title: "Start with a core plan",
  description: "Choose the EloRAG subscription that fits your delivery model and base capacity needs.",
  icon: ShieldCheck
}, {
  id: "02",
  title: "Activate white-label",
  description: "Unlock branded delivery so EloRAG can be presented under your own agency identity.",
  icon: Layers3
}, {
  id: "03",
  title: "Add capacity when needed",
  description: "Use Capacity Packs to expand storage, vectors, or queries in a predictable way.",
  icon: Database
}, {
  id: "04",
  title: "Configure branding",
  description: "Apply your logo, colors, routing, and client-facing presentation across the environment.",
  icon: Palette
}, {
  id: "05",
  title: "Deploy to clients",
  description: "Provision branded client environments while approved-only retrieval and citation logic remain enforced underneath.",
  icon: Rocket
}];
export default function HowWhiteLabelWorks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: stepsRef,
    isVisible: isStepsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="how-white-label-works" className="overflow-hidden bg-background py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div ref={headerRef} className={cn("mx-auto mb-12 max-w-3xl text-center transition-all duration-700 ease-out", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <CheckCircle2 className="size-4" />
            <span>How it works</span>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            How white-label works
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
            A simple operating model for agencies that want to launch branded RAG environments
            without building the control layer themselves.
          </p>
        </div>

        <div ref={stepsRef} className={cn("grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-5 transition-all duration-700 ease-out", isStepsVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
          {steps.map((step, index) => {
          const Icon = step.icon;
          return <Card key={step.id} data-index={index} className="relative flex min-h-[240px] flex-col rounded-2xl border border-border/70 bg-card p-6 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.12)] transition-all duration-300 hover:border-primary/25 hover:shadow-[0_18px_40px_-18px_rgba(0,0,0,0.15)]" style={{
            transitionDelay: `${index * 90}ms`
          }}>
                <div className="mb-5 flex items-center justify-between">
                  <div className="rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-semibold tracking-wider text-muted-foreground">
                    {step.id}
                  </div>

                  <div className="rounded-xl bg-primary/10 p-3">
                    <Icon className="size-5 text-primary" />
                  </div>
                </div>

                <h3 className="mb-3 text-lg font-semibold leading-tight text-foreground">
                  {step.title}
                </h3>

                <p className="text-sm leading-relaxed text-muted-foreground">
                  {step.description}
                </p>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}